class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      window.lumiCartSync?.();
    });

    this.addEventListener(
      'transitionend',
      () => {
        // The branded empty state (snippets/cart-drawer.liquid) no longer uses
        // Dawn's `.drawer__inner-empty` wrapper, so fall back to the drawer inner /
        // #CartDrawer to avoid trapFocus(null) → getFocusableElements crash.
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty') ||
            this.querySelector('.drawer__inner') ||
            document.getElementById('CartDrawer')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    // iOS-safe body scroll lock (reference technique): fix the body at the
    // current scroll offset so the page can't rubber-band behind the drawer,
    // and restore the exact position on close. Dawn's overflow-hidden class
    // alone doesn't stop iOS Safari background scrolling.
    if (!this._bodyLocked) {
      this._bodyLocked = true;
      this._lockScrollY = window.scrollY;
      // Fixing the body takes every sticky bar (Dawn's header, the landing pages' own navs) out of its stuck
      // position, so it slides off with the page. Find the sticky bars touching the top of the screen now and
      // hold each exactly where the shopper sees it, with a relative offset from its normal place in the page.
      this._pinned = [];
      const seen = new Set();
      [0.25, 0.5, 0.75].forEach((fx) => {
        document.elementsFromPoint(window.innerWidth * fx, 4).forEach((hit) => {
          for (let n = hit; n && n !== document.body; n = n.parentElement) {
            if (seen.has(n) || n.closest('cart-drawer')) continue;
            if (getComputedStyle(n).position !== 'sticky') continue;
            seen.add(n);
            const shownTop = n.getBoundingClientRect().top;
            const prev = n.style.position;
            n.style.position = 'static';
            const naturalTop = n.getBoundingClientRect().top;
            n.style.position = prev;
            this._pinned.push({ n, off: shownTop - naturalTop });
          }
        });
      });
      const bodyStyle = document.body.style;
      bodyStyle.position = 'fixed';
      bodyStyle.top = `-${this._lockScrollY}px`;
      bodyStyle.left = '0';
      bodyStyle.right = '0';
      bodyStyle.overflow = 'hidden';
      this._pinned.forEach(({ n, off }) => {
        n.style.position = 'relative';
        n.style.top = `${off}px`;
      });
    }

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    if (this._bodyLocked) {
      this._bodyLocked = false;
      const bodyStyle = document.body.style;
      bodyStyle.position = '';
      bodyStyle.top = '';
      bodyStyle.left = '';
      bodyStyle.right = '';
      bodyStyle.overflow = '';
      (this._pinned || []).forEach(({ n }) => {
        n.style.position = '';
        n.style.top = '';
      });
      this._pinned = [];
      window.scrollTo(0, this._lockScrollY || 0);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // This theme carries the `is-empty` state class on the <cart-drawer> root
    // element (see snippets/cart-drawer.liquid + component-cart(-drawer).css),
    // not on .drawer__inner as stock Dawn does. Sync it from the freshly
    // re-rendered section so the drawer flips out of the empty state (and its
    // line items stop being hidden by `.is-empty .cart__contents`) after an add,
    // and back into it when the last item is removed.
    const newDrawerHTML = parsedState.sections && parsedState.sections['cart-drawer'];
    if (newDrawerHTML) {
      const renderedRoot = new DOMParser()
        .parseFromString(newDrawerHTML, 'text/html')
        .querySelector('cart-drawer');
      if (renderedRoot) this.classList.toggle('is-empty', renderedRoot.classList.contains('is-empty'));
    }
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      // open() early-returns when the drawer is already active, so run the
      // post-render sync (scroll restore, arrow, celebration) here too.
      window.lumiCartSync?.();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
